import { globalStyleObj } from "@/app/assets/styles";
import {
  CommonTextAreaField,
  CommonTextInputField,
  FileReuseDialog,
  LabelText,
} from "@/components";
import { getFileSettingsValue } from "@/utils/website-settings-helper";
import React from "react";

const ServiceCommonFields = ({
  control,
  errors,
  formData,
  filesList,
  allFiles,
  paginationDetails,
  handleOnChangeTextEditor,
  handleOnChangeFile,
  adminRole,
  permissionsList,
  searchValue,
  selectedFileType,
  isItemDetailsExist = false,
  isHideInOtherLanguages = false,
  activeLanguageTab = "en",
}) => {
  return (
    <>
      {/* Service Name */}
      <CommonTextInputField
        fieldName="service_name"
        fieldType="text"
        fieldId={isItemDetailsExist ? "update-service-name" : "service-name"}
        control={control}
        errors={errors}
        errorsType={errors?.service_name}
        placeholderText="Name"
        labelName="Name"
        labelStatus={!isItemDetailsExist}
        inputBoxMaxWidth="xl:max-w-[500px]"
        translateField={isItemDetailsExist}
      />

      {/* Slug */}
      {isHideInOtherLanguages && (
        <CommonTextInputField
          fieldName="slug"
          fieldType="text"
          fieldId={isItemDetailsExist ? "update-service-slug" : "service-slug"}
          control={control}
          errors={errors}
          errorsType={errors?.slug}
          placeholderText="Slug"
          labelName="Slug"
          labelStatus={!isItemDetailsExist}
          extraClass="mt-5"
          inputBoxMaxWidth="xl:max-w-[500px]"
        />
      )}

      {/* Short Description */}
      <CommonTextAreaField
        fieldName="service_short_description"
        fieldId={
          isItemDetailsExist
            ? "update-service-short-description"
            : "service-short-description"
        }
        control={control}
        errors={errors}
        errorsType={errors?.service_short_description}
        labelName="Short Description"
        placeholderText="Short Description"
        labelStatus={!isItemDetailsExist}
        extraClass="mt-5"
        inputBoxMaxWidth="xl:max-w-[500px]"
        translateField={isItemDetailsExist}
      />

      {/* Featured Image */}
      {isHideInOtherLanguages && (
        <div className={`mt-5 ${globalStyleObj.commonInputContainerClass}`}>
          <LabelText
            text="Featured Image"
            htmlForId={
              isItemDetailsExist
                ? "service-featured-image"
                : "service-update-featured-image"
            }
          />

          <div className="flex flex-col gap-2 w-full xl:max-w-[500px]">
            <FileReuseDialog
              htmlId={
                isItemDetailsExist
                  ? "service-featured-image"
                  : "service-update-featured-image"
              }
              filesList={filesList}
              allFiles={allFiles}
              paginationDetails={paginationDetails}
              searchValue={searchValue}
              selectedFileType={selectedFileType}
              onChangeBannerImage={(id) =>
                handleOnChangeFile("featured_image", id)
              }
              selectedBannerFileId={
                isItemDetailsExist
                  ? (getFileSettingsValue(
                      allFiles,
                      formData?.featured_image || ""
                    )?._id ?? null)
                  : null
              }
              selectedBannerFileName={
                isItemDetailsExist
                  ? (getFileSettingsValue(
                      allFiles,
                      formData?.featured_image || ""
                    )?.fileName ?? null)
                  : null
              }
              adminRole={adminRole}
              permissionsList={permissionsList}
            />
          </div>
        </div>
      )}

      {/* Banner Image */}
      {isHideInOtherLanguages && (
        <div className={`mt-5 ${globalStyleObj.commonInputContainerClass}`}>
          <LabelText
            text="Banner Image"
            htmlForId={
              isItemDetailsExist
                ? "service-banner-image"
                : "service-update-banner-image"
            }
          />

          <div className="flex flex-col gap-2 w-full xl:max-w-[500px]">
            <FileReuseDialog
              htmlId={
                isItemDetailsExist
                  ? "service-banner-image"
                  : "service-update-banner-image"
              }
              filesList={filesList}
              allFiles={allFiles}
              paginationDetails={paginationDetails}
              searchValue={searchValue}
              selectedFileType={selectedFileType}
              onChangeBannerImage={(id) =>
                handleOnChangeFile("banner_image", id)
              }
              selectedBannerFileId={
                isItemDetailsExist
                  ? (getFileSettingsValue(
                      allFiles,
                      formData?.banner_image || ""
                    )?._id ?? null)
                  : null
              }
              selectedBannerFileName={
                isItemDetailsExist
                  ? (getFileSettingsValue(
                      allFiles,
                      formData?.banner_image || ""
                    )?.fileName ?? null)
                  : null
              }
              adminRole={adminRole}
              permissionsList={permissionsList}
            />
          </div>
        </div>
      )}

      {/* Description */}
      <CommonTextAreaField
        fieldName="service_description"
        fieldId={
          isItemDetailsExist
            ? "update-service-description"
            : "service-description"
        }
        control={control}
        errors={errors}
        errorsType={errors?.service_description}
        labelName="Description"
        placeholderText="Description"
        extraClass="mt-5"
        inputBoxMaxWidth="xl:max-w-[500px]"
        translateField={isItemDetailsExist}
      />

      {/* Source Link */}
      {isHideInOtherLanguages && (
        <CommonTextInputField
          fieldName="source_link"
          fieldType="text"
          fieldId={
            isItemDetailsExist
              ? "update-service-source-link"
              : "service-source-link"
          }
          control={control}
          errors={errors}
          errorsType={errors?.source_link}
          placeholderText="Source Link"
          labelName="Source Link"
          extraClass="mt-5"
          inputBoxMaxWidth="xl:max-w-[500px]"
          extraInformationText="*Link must be a valid full URL"
        />
      )}

      {/* Button Label */}
      <CommonTextInputField
        fieldName="button_label"
        fieldType="text"
        fieldId={
          isItemDetailsExist
            ? "update-service-button-label"
            : "service-button-label"
        }
        control={control}
        errors={errors}
        errorsType={errors?.button_label}
        placeholderText="Button Label"
        labelName="Button Label"
        extraClass="mt-5"
        inputBoxMaxWidth="xl:max-w-[500px]"
        translateField={isItemDetailsExist}
      />

      {/* Order Number */}
      {isHideInOtherLanguages && (
        <CommonTextInputField
          fieldName="order_number"
          fieldType="text"
          fieldId={
            isItemDetailsExist
              ? "update-service-order-number"
              : "service-order-number"
          }
          control={control}
          errors={errors}
          errorsType={errors?.order_number}
          placeholderText="Order Number"
          labelName="Order Number"
          extraClass="mt-5"
          inputBoxMaxWidth="xl:max-w-[500px]"
          extraInformationText="*Enter higher number for the service to be shown on top OR default is 1"
        />
      )}
    </>
  );
};

export default ServiceCommonFields;
